import {Button, Dialog, DialogActions, DialogContent, LinearProgress, Stack, Typography} from "@mui/material";
import {TextField} from "@variocube/app-ui";
import React, {useMemo, useState} from "react";
import {useAsync, useAsyncCallback} from "react-async-hook";
import {useCenterApi} from "../data/center";
import {BoxAvailability, useSiteApi} from "../data/sites";
import {useCloseOnBack} from "../history-utils";
import {useLocalization} from "../i18n";
import {isBlank, notBlank} from "../utils";
import {QrcodeScanner} from "./qrcode-scanner";

import Location from "../assets/location_r52x.svg";

/**
 * Extracts the cube id from a scanned QR value. The unified per-locker QR encodes a `…/cubes/{cubeId}`
 * URL; legacy stickers encode the bare cube id. Returns the bare id either way.
 */
function extractCubeId(scanned: string): string {
	const match = scanned.match(/\/cubes\/([^/?#]+)/);
	if (!match) {
		return scanned;
	}
	try {
		return decodeURIComponent(match[1]);
	}
	catch {
		// Malformed percent-encoding in a garbage/foreign QR — fall back to the raw segment rather than
		// throwing an uncaught URIError out of the scan handler.
		return match[1];
	}
}

interface LocationVerificationProps {
	bookingId: string;
	onCancel: () => void;
	onVerified: (cubeId: string, availability: BoxAvailability) => void;
	cubeDescription?: string;
	qrcodeReaderEnabled?: boolean;
	requiredCubeId?: string;
}

export function LocationVerification(props: LocationVerificationProps) {
	const {
		bookingId,
		onCancel,
		onVerified,
		cubeDescription,
		qrcodeReaderEnabled,
		requiredCubeId,
	} = props;
	const {t} = useLocalization();
	const {getCodeShape, getCubeByCode} = useCenterApi();
	const {verifyBookingLocation} = useSiteApi();

	const [scannedValue, setScannedValue] = useState("");
	const [code, setCode] = useState("");

	// The code shape only matters for the typed location code, which the QR mode does not offer.
	const {result: codeShape} = useAsync(
		async () => qrcodeReaderEnabled ? undefined : await getCodeShape(),
		[qrcodeReaderEnabled],
	);

	const {result: cube, error: cubeError, loading: cubeLoading, execute: checkCube, reset: resetCube} =
		useAsyncCallback(getCubeByCode);
	const {error: locationError, loading: locationLoading, execute: verifyLocation, reset: resetLocation} =
		useAsyncCallback(verifyBookingLocation);

	const verificationError = useMemo(() =>
		cubeError !== undefined
		|| locationError !== undefined
		|| (notBlank(requiredCubeId) && notBlank(scannedValue) && requiredCubeId !== scannedValue)
		|| (notBlank(requiredCubeId) && notBlank(cube?.hostname) && requiredCubeId !== cube?.hostname), [
		cubeError,
		locationError,
		requiredCubeId,
		scannedValue,
		cube,
	]);

	function handleCodeScanned(value: string) {
		// The unified per-locker QR (admin tab / cube display) now encodes a `…/cubes/{cubeId}` URL,
		// while older stickers encode the bare cube id. Accept both: extract the id from the URL form
		// so verification (which compares against requiredCubeId) keeps working on either sticker.
		const cubeId = extractCubeId(value);
		setScannedValue(cubeId);
		console.info("Scanned QR-Code", value, "->", cubeId);
		// Gate the scan on the required cube exactly like the typed-code path: the backend only validates site
		// membership, so scanning a *different* cube of the same site would otherwise "pass" and fire
		// onVerified(wrongCubeId). Setting scannedValue above surfaces the mismatch via the error dialog.
		if (isBlank(requiredCubeId) || cubeId === requiredCubeId) {
			handleVerifyLocation(cubeId);
		}
	}

	async function handleCodeTyped(value: string) {
		const typed = value.replace(/\D/g, "");
		if (codeShape && typed.length >= codeShape.length) {
			try {
				const cube = await checkCube(typed);
				if (cube !== undefined && (isBlank(requiredCubeId) || cube?.hostname === requiredCubeId)) {
					handleVerifyLocation(cube.hostname);
				}
			}
			catch (err) {
			}
		}
		setCode(typed);
	}

	function handleVerifyLocation(cubeId: string) {
		verifyLocation(bookingId, cubeId)
			.then(availability => onVerified(cubeId, availability))
			.catch(console.error);
	}

	function reset() {
		setScannedValue("");
		setCode("");
		resetCube();
		resetLocation();
	}

	useCloseOnBack(verificationError, reset);

	return (
		<>
			<Stack
				direction="column"
				alignItems="center"
				gap={2}
			>
				<Location style={{width: "150px", height: "auto"}} />
				<Typography>
					{qrcodeReaderEnabled
						? t("location.verification.messageQrCode")
						: t("location.verification.messageLocationCode")}
				</Typography>
				{notBlank(cubeDescription) && <Typography variant="subtitle1">{cubeDescription}</Typography>}
				{
					/* QR codes are used on locker banks without a display, where no location code can be shown — the
				 two verification means are mutually exclusive, never offered side by side. */
				}
				{qrcodeReaderEnabled
					? (
						<QrcodeScanner
							onScanned={handleCodeScanned}
							paused={cubeLoading || locationLoading || verificationError}
						/>
					)
					: (
						<TextField
							size="small"
							label={t("location.useLocationCode")}
							value={code}
							type="number"
							onChange={handleCodeTyped}
							disabled={cubeLoading || locationLoading}
						/>
					)}
				{/* Always mounted (hidden when idle) so verification progress doesn't shift the layout. */}
				<LinearProgress
					sx={{alignSelf: "stretch", visibility: locationLoading ? "visible" : "hidden"}}
				/>
				<Button
					variant="contained"
					color="inherit"
					disableElevation
					onClick={onCancel}
				>
					{t("actions.cancel")}
				</Button>
			</Stack>
			<Dialog open={verificationError} onClose={reset}>
				<DialogContent>
					<Typography>{t("location.verification.error")}</Typography>
					{cubeDescription && <Typography>{t("location.verification.hint")} {cubeDescription}</Typography>}
				</DialogContent>
				<DialogActions>
					<Button
						size="small"
						variant="contained"
						onClick={reset}
						disableElevation
					>
						{t("actions.close")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
